import * as ImagePicker from "expo-image-picker";
import { FC, useEffect, useRef, useState } from "react";

import {
  DropdownPicker,
  HStack,
  Input,
  InputField,
  MediaPicker,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Question, QuestionDraft, Rule } from "@/types/resources";
import { QuestionSuggestion } from "@/types/responses";
import {
  RulesEditor,
  conditionSourceQuestions,
  hasApplicableRules,
} from "../rules";
import AdvancedSettings from "./AdvancedSettings";
import QuestionField from "./QuestionField";
import QuestionTextInput from "./QuestionTextInput";

const MAX_MEDIA = 10;
const DEFAULT_SCALE = 5;
const MIN_SCALE = 3;
const MAX_SCALE = 10;

interface LinearScaleQuestionEditorProps {
  allQuestions?: Question[];
  currentQuestionOrder?: number;
  disabled?: boolean;
  onChange: (question: QuestionDraft<"linear_scale">) => void;
  question: QuestionDraft<"linear_scale">;
}

/**
 * options = ["1", "2", "3", "4", "5"] when no labels are set.
 * options[i] === String(i+1) means "no custom label" for that index.
 */
const getScaleSize = (options: string[]): number | null => {
  const size = options.length;
  if (size < MIN_SCALE) return null;
  return size <= MAX_SCALE ? size : DEFAULT_SCALE;
};

/**
 * Generate options array for a given scale size, preserving existing labels
 */
const isDefaultLabel = (value: string, index: number): boolean =>
  value === String(index + 1);

const generateOptions = (size: number, existing: string[] = []): string[] => {
  const firstLabel = existing[0] ?? String(1);
  const lastLabel =
    existing.length > 0 ? existing[existing.length - 1] : String(size);
  const oldLastIdx = existing.length - 1;
  return Array.from({ length: size }, (_, i) => {
    if (i === 0) return firstLabel;
    if (i === size - 1) return lastLabel;
    // If this slot was the old last position, reset it to its default
    if (i === oldLastIdx) return String(i + 1);
    return existing[i] ?? String(i + 1);
  });
};

const LinearScaleQuestionEditor: FC<LinearScaleQuestionEditorProps> = ({
  allQuestions = [],
  currentQuestionOrder,
  disabled = false,
  onChange,
  question,
}) => {
  const { i18n } = useLocalization();
  const [isSuggesting, setIsSuggesting] = useState(false);
  const isDisabled = disabled || isSuggesting;

  const scaleSize = getScaleSize(question.options);
  const rules = question.rules || [];

  const [localLabels, setLocalLabels] = useState<string[]>(() =>
    question.options.map((opt, i) => (isDefaultLabel(opt, i) ? "" : opt))
  );
  const prevOptionsLength = useRef(question.options.length);

  useEffect(() => {
    if (question.options.length !== prevOptionsLength.current) {
      prevOptionsLength.current = question.options.length;
      setLocalLabels(
        question.options.map((opt, i) => (isDefaultLabel(opt, i) ? "" : opt))
      );
    }
  }, [question.options]);

  const previousConditionQuestions = conditionSourceQuestions(
    allQuestions,
    currentQuestionOrder
  );

  const handleQuestionTextChange = (text: string) => {
    onChange({ ...question, text });
  };

  const handleSuggest = (suggestion: QuestionSuggestion) => {
    const newOptions =
      suggestion.options.length === 2
        ? generateOptions(scaleSize ?? DEFAULT_SCALE, [
            suggestion.options[0],
            ...Array(Math.max(0, (scaleSize ?? DEFAULT_SCALE) - 2)).fill(""),
            suggestion.options[1],
          ])
        : question.options;
    onChange({ ...question, options: newOptions, text: suggestion.text });
  };

  const handleScaleSizeChange = (value: string) => {
    const size = parseInt(value, 10);
    onChange({
      ...question,
      options: generateOptions(size, question.options),
    });
  };

  const handleLabelChange = (index: number, label: string) => {
    const newLocal = [...localLabels];
    newLocal[index] = label;
    setLocalLabels(newLocal);
    const newOptions = [...question.options];
    newOptions[index] = label || String(index + 1);
    onChange({ ...question, options: newOptions });
  };

  const handleMediaChange = (
    newMedia: (ImagePicker.ImagePickerAsset | string)[]
  ) => {
    onChange({ ...question, media: newMedia });
  };

  const handleRulesChange = (newRules: Rule[]) => {
    onChange({ ...question, rules: newRules });
  };

  // Generate scale dropdown options (3-10)
  const scaleOptions = [];
  for (let i = MIN_SCALE; i <= MAX_SCALE; i++) {
    scaleOptions.push({ label: String(i), value: String(i) });
  }

  return (
    <MediaPicker
      isDisabled={isDisabled}
      maxItems={MAX_MEDIA}
      media={question.media}
      onMediaChange={handleMediaChange}
    >
      <VStack className="gap-6">
        <VStack className="gap-3">
          <QuestionField order={currentQuestionOrder}>
            <QuestionTextInput
              disabled={disabled}
              lines={question.lines}
              onChange={handleQuestionTextChange}
              onLoadingChange={setIsSuggesting}
              onSuggest={handleSuggest}
              options={question.options}
              text={question.text || ""}
              type={question.type}
            />
            <MediaPicker.Trigger compact height={70} width={70} />
          </QuestionField>
          <MediaPicker.Thumbnails />
          <MediaPicker.Error />
        </VStack>

        {/* Scale */}
        <VStack className="gap-3">
          <HStack className="items-center gap-1">
            <Text className="text-sm font-semibold text-typography-800">
              {i18n.t("questions.scaleLabel")}
            </Text>
            <Text className="text-sm font-semibold text-error-500">*</Text>
          </HStack>
          <HStack className="gap-4 pl-4 items-center">
            <DropdownPicker
              className="w-24"
              isDisabled={isDisabled}
              items={scaleOptions}
              onChange={handleScaleSizeChange}
              placeholder={i18n.t("questions.selectScale")}
              selectKey={`linear-scale-${scaleSize}`}
              value={scaleSize !== null ? String(scaleSize) : null}
            />
          </HStack>
        </VStack>

        {/* Labels */}
        {scaleSize !== null && (
          <VStack className="gap-3">
            <Text className="text-sm font-semibold text-typography-800">
              {i18n.t("questions.labelsTitle")}
            </Text>
            <VStack className="gap-2 pl-4">
              {question.options.map((label, index) => {
                const isFirstOrLast =
                  index === 0 || index === question.options.length - 1;
                return (
                  <HStack className="gap-2 items-center" key={index}>
                    <HStack className="items-center w-8">
                      <Text className="text-sm text-typography-600">
                        {index + 1} =
                      </Text>
                      {isFirstOrLast && (
                        <Text className="text-sm font-semibold text-error-500">
                          *
                        </Text>
                      )}
                    </HStack>
                    <Input
                      className="flex-1 border border-outline-200 rounded-xl bg-background-50"
                      isDisabled={isDisabled}
                    >
                      <InputField
                        onChangeText={(text: string) =>
                          handleLabelChange(index, text)
                        }
                        placeholder={`${i18n.t("questions.linearScale.label")} ${index + 1}`}
                        value={localLabels[index] ?? ""}
                      />
                    </Input>
                  </HStack>
                );
              })}
            </VStack>
          </VStack>
        )}

        {/* Skip logic */}
        {hasApplicableRules(allQuestions, currentQuestionOrder) && (
          <AdvancedSettings defaultExpanded={rules.length > 0}>
            <RulesEditor
              allQuestions={allQuestions}
              currentQuestionOrder={currentQuestionOrder}
              disabled={isDisabled}
              onRulesChange={handleRulesChange}
              previousConditionQuestions={previousConditionQuestions}
              rules={rules}
            />
          </AdvancedSettings>
        )}
      </VStack>
    </MediaPicker>
  );
};

export default LinearScaleQuestionEditor;
